import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Check, Loader2, Plus, X, Bot, ExternalLink,
  AlertTriangle, Trash2, Save, Undo2, Pause, CheckCircle2, Play,
} from "lucide-react";
import { useState, useEffect } from "react";
import { SkillSelector } from "@/components/SkillSelector";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { EMPLOYMENT_TYPES, WORK_MODELS, JOB_STATUS_LABELS } from "@/lib/constants";
import { getJobQuality, QUALITY_STYLES } from "@/lib/jobQuality";
import { isCrawlerJob } from "@/lib/jobUtils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ALL_STATUSES = [
  { value: "rascunho", label: "Rascunho" },
  { value: "pending_review", label: "Enriquecendo" },
  { value: "em_revisao", label: "Em revisão" },
  { value: "publicada", label: "Publicada" },
  { value: "pausada", label: "Pausada" },
  { value: "encerrada", label: "Encerrada" },
  { value: "descartada", label: "Descartada" },
];

export default function AdminTodasVagasEditar() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [niceSkills, setNiceSkills] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [newBenefit, setNewBenefit] = useState("");
  const [processSteps, setProcessSteps] = useState<string[]>([]);
  const [newStep, setNewStep] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [formData, setFormData] = useState({
    titulo: "", tipo: "", modelo: "", cidade: "", uf: "", descricao: "",
    responsabilidades: "", requisitosObrigatorios: "", requisitosDesejaveis: "",
    prazo: "", stipendMin: "", stipendMax: "",
  });

  const { data: post, isLoading: postLoading } = useQuery({
    queryKey: ["todas_vagas_detail", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_posts")
        .select("*, partner_orgs(name), crawler_metadata(company_name, source_name, source_url, external_apply_url)")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (post && !loaded) {
      const p = post as any;
      setFormData({
        titulo: post.title || "",
        tipo: post.employment_type || "",
        modelo: post.work_model || "",
        cidade: post.location_city || "",
        uf: post.location_state || "",
        descricao: post.description || "",
        responsabilidades: Array.isArray(post.responsibilities) ? (post.responsibilities as string[]).join("\n") : "",
        requisitosObrigatorios: Array.isArray(post.requirements_must) ? (post.requirements_must as string[]).join("\n") : "",
        requisitosDesejaveis: Array.isArray(p.requirements_nice) ? (p.requirements_nice as string[]).join("\n") : "",
        prazo: post.deadline_date || "",
        stipendMin: p.stipend_min ? String(p.stipend_min) : "",
        stipendMax: p.stipend_max ? String(p.stipend_max) : "",
      });
      setRequiredSkills(Array.isArray(post.required_skills) ? (post.required_skills as string[]) : []);
      setNiceSkills(Array.isArray(post.nice_skills) ? (post.nice_skills as string[]) : []);
      setBenefits(Array.isArray(p.benefits) ? p.benefits : []);
      setProcessSteps(Array.isArray(p.process_steps) ? p.process_steps : []);
      setSelectedStatus(post.status);
      setLoaded(true);
    }
  }, [post, loaded]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "requisitosObrigatorios") {
      const terms = ["anos de experiência", "experiência prévia", "experiência comprovada"];
      setShowWarning(terms.some((t) => value.toLowerCase().includes(t)));
    }
  };

  const addBenefit = () => { if (newBenefit.trim()) { setBenefits(prev => [...prev, newBenefit.trim()]); setNewBenefit(""); } };
  const addStep = () => { if (newStep.trim()) { setProcessSteps(prev => [...prev, newStep.trim()]); setNewStep(""); } };

  const buildPayload = () => ({
    title: formData.titulo,
    employment_type: formData.tipo || "aprendiz",
    work_model: formData.modelo || null,
    location_city: formData.cidade || null,
    location_state: formData.uf || null,
    description: formData.descricao || null,
    responsibilities: formData.responsabilidades ? formData.responsabilidades.split("\n").filter(Boolean) : null,
    requirements_must: formData.requisitosObrigatorios ? formData.requisitosObrigatorios.split("\n").filter(Boolean) : null,
    requirements_nice: formData.requisitosDesejaveis ? formData.requisitosDesejaveis.split("\n").filter(Boolean) : [],
    required_skills: requiredSkills.length > 0 ? requiredSkills : null,
    nice_skills: niceSkills.length > 0 ? niceSkills : null,
    deadline_date: formData.prazo || null,
    stipend_min: formData.stipendMin ? (parseInt(formData.stipendMin, 10) || null) : null,
    stipend_max: formData.stipendMax ? (parseInt(formData.stipendMax, 10) || null) : null,
    benefits: benefits.length > 0 ? benefits : [],
    process_steps: processSteps.length > 0 ? processSteps : [],
  });

  const handleSave = async () => {
    if (!id) return;
    if (!formData.titulo.trim()) {
      toast({ title: "Título é obrigatório", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const payload: any = buildPayload();
      payload.status = selectedStatus;
      if (selectedStatus === "publicada" && post?.status !== "publicada") {
        payload.published_at = new Date().toISOString();
      }
      const { error } = await supabase.from("job_posts").update(payload).eq("id", id);
      if (error) throw error;
      queryClient.invalidateQueries({ queryKey: ["admin_all_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_review_jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin_active_jobs"] });
      toast({ title: "Vaga salva com sucesso!" });
      navigate("/admin/todas-vagas");
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) return;
      await supabase.from("crawler_metadata").delete().eq("job_post_id", id);
      await supabase.from("job_applications").delete().eq("job_id", id);
      const { error } = await supabase.from("job_posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_all_jobs"] });
      toast({ title: "Vaga excluída permanentemente" });
      navigate("/admin/todas-vagas");
    },
    onError: (err) => {
      toast({ title: "Erro ao excluir", description: (err as Error).message, variant: "destructive" });
    },
  });

  if (postLoading) {
    return <div className="p-8 flex justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }
  if (!post) {
    return <div className="p-8 text-center"><p className="text-muted-foreground">Vaga não encontrada.</p></div>;
  }

  const p = post as any;
  const meta = p.crawler_metadata?.[0];
  const isCrawler = isCrawlerJob(post);
  const quality = getJobQuality(post);
  const qStyle = QUALITY_STYLES[quality.tier];
  const orgName = p.partner_orgs?.name ?? "—";
  const sl = JOB_STATUS_LABELS[post.status] ?? { label: post.status, variant: "outline" as const };

  return (
    <div className="p-8 max-w-3xl">
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/todas-vagas")}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold text-foreground">Editar Vaga</h1>
          <p className="text-muted-foreground text-sm">
            Org: {orgName} · Status atual: <Badge variant={sl.variant} className="ml-1">{sl.label}</Badge>
          </p>
        </div>
      </motion.div>

      {/* Crawler source info */}
      {isCrawler && meta && (
        <div className="bg-muted/50 rounded-2xl p-4 border border-border mb-6">
          <div className="flex items-start gap-3 flex-wrap">
            <Bot className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-foreground font-medium block">{meta.company_name ?? "—"}</span>
              {meta.source_name && <span className="text-muted-foreground text-xs">Fonte: {meta.source_name}</span>}
            </div>
            <div className="flex gap-2 flex-wrap shrink-0">
              {meta.source_url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={meta.source_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Ver listagem
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quality indicator */}
      <div className={`rounded-xl p-4 border mb-6 flex items-center gap-3 ${qStyle.bg}`}>
        <div className={`w-2.5 h-2.5 rounded-full ${qStyle.dot}`} />
        <span className={`text-sm font-medium ${qStyle.text}`}>Qualidade {qStyle.label} — {quality.filled}/{quality.total} campos</span>
        {quality.missing.length > 0 && (
          <span className="text-xs text-muted-foreground ml-2">Faltam: {quality.missing.join(", ")}</span>
        )}
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
        {/* Status selector */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-2">Status</h2>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALL_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Basic info */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Informações Básicas</h2>
          <div className="space-y-2">
            <Label htmlFor="titulo">Título da vaga *</Label>
            <Input id="titulo" value={formData.titulo} onChange={(e) => handleChange("titulo", e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <div className="flex flex-wrap gap-2">
                {EMPLOYMENT_TYPES.map((t) => (
                  <Badge key={t.value} variant={formData.tipo === t.value ? "interest-selected" : "interest"} className="cursor-pointer" onClick={() => handleChange("tipo", t.value)}>
                    {formData.tipo === t.value && <Check className="w-3 h-3" />} {t.label}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Modelo *</Label>
              <div className="flex flex-wrap gap-2">
                {WORK_MODELS.map((m) => (
                  <Badge key={m.value} variant={formData.modelo === m.value ? "interest-selected" : "interest"} className="cursor-pointer" onClick={() => handleChange("modelo", m.value)}>
                    {formData.modelo === m.value && <Check className="w-3 h-3" />} {m.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          {formData.modelo !== "remoto" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Cidade</Label><Input value={formData.cidade} onChange={(e) => handleChange("cidade", e.target.value)} /></div>
              <div className="space-y-2"><Label>UF</Label><Input maxLength={2} value={formData.uf} onChange={(e) => handleChange("uf", e.target.value.toUpperCase())} /></div>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Descrição</h2>
          <Textarea placeholder="Descreva a vaga..." value={formData.descricao} onChange={(e) => handleChange("descricao", e.target.value)} rows={4} />
          <Label>Responsabilidades (uma por linha)</Label>
          <Textarea placeholder="Responsabilidades..." value={formData.responsabilidades} onChange={(e) => handleChange("responsabilidades", e.target.value)} rows={3} />
        </div>

        {/* Requirements */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Requisitos</h2>
          <Label>Requisitos obrigatórios (um por linha)</Label>
          <Textarea value={formData.requisitosObrigatorios} onChange={(e) => handleChange("requisitosObrigatorios", e.target.value)} rows={3} />
          {showWarning && (
            <div className="bg-warning/10 rounded-xl p-4 border border-warning/20 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning shrink-0" />
              <p className="text-sm font-medium text-foreground">Evite exigir experiência prévia</p>
            </div>
          )}
          <Label>Requisitos desejáveis (um por linha)</Label>
          <Textarea value={formData.requisitosDesejaveis} onChange={(e) => handleChange("requisitosDesejaveis", e.target.value)} rows={2} />
        </div>

        {/* Skills */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-6">
          <h2 className="font-heading font-semibold text-foreground mb-2">Habilidades</h2>
          <SkillSelector selectedSkills={requiredSkills} onChange={setRequiredSkills} maxSkills={6} label="Obrigatórias (até 6)" />
          <SkillSelector selectedSkills={niceSkills} onChange={(s) => setNiceSkills(s.filter((x) => !requiredSkills.includes(x)))} maxSkills={8} label="Desejáveis (até 8)" />
        </div>

        {/* Compensation */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Remuneração e Benefícios</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Bolsa mínima (R$)</Label><Input type="number" value={formData.stipendMin} onChange={(e) => handleChange("stipendMin", e.target.value)} /></div>
            <div className="space-y-2"><Label>Bolsa máxima (R$)</Label><Input type="number" value={formData.stipendMax} onChange={(e) => handleChange("stipendMax", e.target.value)} /></div>
          </div>
          <div className="space-y-2">
            <Label>Benefícios</Label>
            <div className="flex gap-2">
              <Input placeholder="Ex: Vale-transporte" value={newBenefit} onChange={(e) => setNewBenefit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBenefit(); } }} />
              <Button type="button" variant="outline" size="icon" onClick={addBenefit}><Plus className="w-4 h-4" /></Button>
            </div>
            {benefits.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {benefits.map((b, i) => (<Badge key={i} variant="secondary" className="gap-1">{b} <X className="w-3 h-3 cursor-pointer" onClick={() => setBenefits(prev => prev.filter((_, j) => j !== i))} /></Badge>))}
              </div>
            )}
          </div>
        </div>

        {/* Process steps */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <h2 className="font-heading font-semibold text-foreground mb-4">Etapas do Processo Seletivo</h2>
          <div className="flex gap-2">
            <Input placeholder="Ex: Inscrição online" value={newStep} onChange={(e) => setNewStep(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStep(); } }} />
            <Button type="button" variant="outline" size="icon" onClick={addStep}><Plus className="w-4 h-4" /></Button>
          </div>
          {processSteps.length > 0 && (
            <ol className="space-y-2 mt-2">
              {processSteps.map((s, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="flex-1 text-foreground">{s}</span>
                  <X className="w-4 h-4 text-muted-foreground cursor-pointer" onClick={() => setProcessSteps(prev => prev.filter((_, j) => j !== i))} />
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Deadline */}
        <div className="bg-card rounded-2xl p-6 border border-border space-y-4">
          <Label>Prazo para candidatura</Label>
          <Input type="date" value={formData.prazo} onChange={(e) => handleChange("prazo", e.target.value)} />
        </div>

        {/* Action bar */}
        <div className="flex gap-3 flex-wrap sticky bottom-4 bg-background/95 backdrop-blur rounded-2xl p-4 border border-border shadow-lg">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={isLoading || deleteMutation.isPending}
            onClick={() => setConfirmDelete(true)}
          >
            <Trash2 className="w-4 h-4" /> Excluir
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={() => { setSelectedStatus("descartada"); handleSave(); }}
          >
            <X className="w-4 h-4" /> Descartar
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={() => { setSelectedStatus("pending_review"); handleSave(); }}
          >
            <Undo2 className="w-4 h-4" /> Devolver p/ enriquecimento
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={() => { setSelectedStatus("em_revisao"); handleSave(); }}
          >
            <Undo2 className="w-4 h-4" /> Devolver p/ revisão
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLoading}
            onClick={() => { setSelectedStatus("pausada"); handleSave(); }}
          >
            <Pause className="w-4 h-4" /> Pausar
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isLoading}
            onClick={() => { setSelectedStatus("publicada"); handleSave(); }}
          >
            <CheckCircle2 className="w-4 h-4" /> Publicar
          </Button>
          <div className="flex-1" />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/todas-vagas")}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="hero"
            size="sm"
            disabled={isLoading}
            onClick={handleSave}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar
          </Button>
        </div>
      </form>

      {/* Confirm permanent delete */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir vaga permanentemente?</AlertDialogTitle>
            <AlertDialogDescription>
              A vaga <strong>"{formData.titulo}"</strong> será excluída permanentemente do sistema. Esta ação <strong>não pode ser desfeita</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteMutation.mutate()}
            >
              Excluir permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
